// Nine Lives '84 - kinematic platformer physics.
//
// Bodies are integrated one axis at a time and pushed out of solid
// platforms; one-way platforms catch a body only when it falls onto them
// from above. The street at y = 0 and the screen edges are always solid.

import { GRAVITY, MAX_FALL, WORLD_W } from './constants';
import type { Body, Box, Platform } from './types';

const EPS = 0.01;

export function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function xOverlap(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x;
}

/**
 * Advance a body by `dt` seconds. With `ignoreOneWay` set the body falls
 * through one-way platforms (dropping off a fence or clothesline).
 */
export function stepBody(
  b: Body,
  platforms: readonly Platform[],
  dt: number,
  ignoreOneWay = false,
): void {
  const prevY = b.y;
  b.vy -= GRAVITY * dt;
  if (b.vy < -MAX_FALL) b.vy = -MAX_FALL;

  // Horizontal move, then push out of solids sideways.
  b.x += b.vx * dt;
  for (const p of platforms) {
    if (p.oneWay || !overlaps(b, p)) continue;
    if (b.vx > 0) b.x = p.x - b.w;
    else if (b.vx < 0) b.x = p.x + p.w;
    b.vx = 0;
  }
  if (b.x < 0) {
    b.x = 0;
    if (b.vx < 0) b.vx = 0;
  } else if (b.x + b.w > WORLD_W) {
    b.x = WORLD_W - b.w;
    if (b.vx > 0) b.vx = 0;
  }

  // Vertical move: land on tops, bump heads on undersides of solids.
  b.y += b.vy * dt;
  b.onGround = false;
  b.ground = null;
  for (const p of platforms) {
    if (!xOverlap(b, p)) continue;
    const top = p.y + p.h;
    if (p.oneWay) {
      if (ignoreOneWay) continue;
      if (b.vy <= 0 && prevY >= top - EPS && b.y < top) {
        land(b, p, top);
      }
    } else if (overlaps(b, p)) {
      if (b.vy <= 0 && prevY >= top - EPS) {
        land(b, p, top);
      } else if (b.vy > 0 && prevY + b.h <= p.y + EPS) {
        b.y = p.y - b.h;
        b.vy = 0;
      }
    }
  }
  if (b.y <= 0) {
    b.y = 0;
    b.vy = 0;
    b.onGround = true;
    b.ground = null;
  }
}

function land(b: Body, p: Platform, top: number): void {
  b.y = top;
  b.vy = 0;
  b.onGround = true;
  b.ground = p;
}

/** Move `v` toward `target` by at most `step`. */
export function approach(v: number, target: number, step: number): number {
  if (v < target) return Math.min(v + step, target);
  if (v > target) return Math.max(v - step, target);
  return v;
}
